import { User } from '@/@types/user'
import NavBar from '@/components/NavBar'
import Page from '@/components/Page'
import { Tab } from '@/components/Sidebar/config'
import UserProfile from '@/components/UserProfile'
import { useScrollToTop } from '@/utils/dom'
import { getUserQueryOptions } from '@/utils/fetchers/user'
import usePageNeedRefreshSubscriber from '@/utils/hooks/usePageNeedRefreshSubscriber'
import { useCurrentUser } from '@/utils/hooks/useUser'
import queryClient from '@/utils/queryClient'
import { useSuspenseQuery, UseSuspenseQueryOptions } from '@tanstack/react-query'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { motion } from 'motion/react'
import toast from 'react-hot-toast'
import { useTitle } from 'react-use'
import MoreDropdownButton from './-components/MoreDropdownButton'
import UserPageTabs from './-components/UserPageTabs'
import styles from './route.module.css'

export const Route = createFileRoute('/u/$username')({
	component: UserProfilePage,
	loader: ({ params: { username } }) => queryClient.ensureQueryData(getUserQueryOptions(username)),
	remountDeps: ({ params: { username } }) => username,
})

export default function UserProfilePage() {
	const { username } = Route.useParams()
	const currentUser = useCurrentUser()
	const isMyUserProfilePage = username === currentUser?.username
	const { data: user } = useSuspenseQuery(
		getUserQueryOptions(username) as UseSuspenseQueryOptions<User>
	)

	const scrollToTop = useScrollToTop()

	usePageNeedRefreshSubscriber(Tab.USER_PROFILE, () => {
		scrollToTop()
		toast.promise(
			queryClient.invalidateQueries({ queryKey: getUserQueryOptions(username).queryKey }),
			{
				loading: '加载中...',
				success: '加载成功',
				error: '加载失败',
			}
		)
	})

	useTitle(`${isMyUserProfilePage ? '我的主页' : `${user.screenName}的主页`} - 即刻`)

	if (!user) {
		return null
	}

	return (
		<Page
			header={
				<NavBar
					isImmersive
					showBackButton={!isMyUserProfilePage}
					renderRightSection={({ isScrolled }) =>
						!isMyUserProfilePage && <MoreDropdownButton username={username} variant="glass" />
					}
					className={styles.navBar}
				/>
			}
		>
			<motion.div
				style={{
					y: 'var(--user-profile-content-offset-y)',
				}}
				className={styles.content}
			>
				<UserProfile data={user} />
				<section>
					<UserPageTabs username={username} />
					<Outlet />
				</section>
			</motion.div>
		</Page>
	)
}
